<template>
  <div class="container-fluid">
    <!-- Header with breadcrumb-style navigation -->
    <div class="mb-4">
      <div class="d-flex align-items-center">
        <button 
          class="btn btn-link text-muted p-0 me-2"
          @click="router.push('/databank')"
          style="text-decoration: none;"
        >
          Databank
        </button>
        <span class="text-muted me-2">/</span>
        <h2 class="mb-0">{{ $t('databank.mailTemplate.title') }}</h2>
      </div>
    </div>

    <!-- Alert Messages -->
    <div v-if="error" class="alert alert-danger alert-dismissible fade show" role="alert">
      {{ error }}
      <button type="button" class="btn-close" @click="error = null"></button>
    </div>
    
    <div v-if="success" class="alert alert-success alert-dismissible fade show" role="alert">
      {{ success }}
      <button type="button" class="btn-close" @click="success = null"></button>
    </div>

    <!-- Data Table -->
    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-header d-flex justify-content-between align-items-center">
            <div class="d-flex align-items-center gap-3">
              <h5 class="mb-0">
                {{ $t('databank.mailTemplate.existingRecords') }}
              </h5>
              <div class="position-relative" style="width: 250px;">
                <input
                  type="text"
                  class="form-control form-control-sm pe-4"
                  v-model="searchQuery"
                  :placeholder="$t('common.actions.search')"
                />
                <button
                  v-if="searchQuery"
                  type="button"
                  class="btn-close btn-close-sm position-absolute top-50 end-0 translate-middle-y me-2"
                  style="font-size: 0.7rem; opacity: 0.6;"
                  @click="clearSearch"
                  :title="$t('common.clear')"
                ></button>
              </div>
            </div>
            <div class="d-flex gap-2">
              <button class="btn btn-outline-primary" @click="loadMailTemplates()">
                <i class="ph-duotone ph-arrow-clockwise"></i>
              </button>
            </div>
          </div>
          <div class="card-body">
            <div v-if="isLoadingData" class="text-center py-4">
              <div class="spinner-border text-primary" role="status">
                <span class="visually-hidden">{{ $t('common.loading') }}</span>
              </div>
            </div>
            
            <div v-else-if="filteredMailTemplates.length === 0" class="text-center py-4 text-muted">
              <i class="ph-duotone ph-envelope text-muted" style="font-size: 2rem;"></i>
              <p class="mb-0">{{ $t('databank.mailTemplate.noRecords') }}</p>
            </div>
            
            <div v-else class="table-responsive">
              <table class="table table-hover">
                <thead class="table-light">
                  <tr>
                    <th>{{ $t('databank.mailTemplate.key') }}</th>
                    <th>{{ $t('databank.mailTemplate.subject') }}</th>
                    <th>{{ $t('common.createdAt') }}</th>
                    <th>{{ $t('common.actionsLabel') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="template in filteredMailTemplates" :key="template.id">
                    <td>
                      <code v-if="template.templateKey">{{ template.templateKey }}</code>
                      <span v-else class="text-muted">-</span>
                    </td>
                    <td>
                      <strong>{{ template.subject || '-' }}</strong>
                    </td>
                    <td>
                      <small class="text-muted">{{ formatDate(template.createdAt) }}</small>
                    </td>
                    <td>
                      <div class="d-flex gap-2">
                        <button 
                          class="btn btn-sm btn-outline-primary"
                          @click="editMailTemplate(template)"
                          :title="$t('common.edit')"
                          :disabled="isLoading"
                        >
                          <span v-if="isLoading && editingId === template.id" class="spinner-border spinner-border-sm me-1"></span>
                          <i v-else class="ph-duotone ph-pencil-simple"></i>
                        </button>
                        <button 
                          class="btn btn-sm btn-outline-warning"
                          @click="restoreMailTemplate(template)"
                          :title="$t('databank.mailTemplate.restore')"
                          :disabled="isLoading"
                        >
                          <i class="ph-duotone ph-arrow-counter-clockwise"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Add/Edit Mail Template Modal -->
    <div class="modal fade" id="mailTemplateModal" tabindex="-1" aria-labelledby="mailTemplateModalLabel" aria-hidden="true">
      <div class="modal-dialog modal-fullscreen">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="mailTemplateModalLabel">
              {{ isEditing ? $t('databank.mailTemplate.editTitle') : $t('databank.mailTemplate.addTitle') }}
            </h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <form @submit.prevent="submitForm">
              <div class="row">
                <!-- Template Key -->
                <div class="col-md-6 mb-3">
                  <label class="form-label">
                    {{ $t('databank.mailTemplate.key') }}
                    <span class="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    class="form-control"
                    v-model="form.templateKey"
                    :placeholder="$t('databank.mailTemplate.keyPlaceholder')"
                    :class="{ 'is-invalid': v$.templateKey.$error }"
                    :disabled="isEditing"
                    required
                  />
                  <div v-if="v$.templateKey.$error" class="invalid-feedback">
                    {{ v$.templateKey.$errors[0].$message }}
                  </div>
                </div>

                <!-- Subject -->
                <div class="col-md-6 mb-3">
                  <label class="form-label">
                    {{ $t('databank.mailTemplate.subject') }}
                    <span class="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    class="form-control"
                    v-model="form.subject"
                    :placeholder="$t('databank.mailTemplate.subjectPlaceholder')"
                    :class="{ 'is-invalid': v$.subject.$error }"
                    required
                  />
                  <div v-if="v$.subject.$error" class="invalid-feedback">
                    {{ v$.subject.$errors[0].$message }}
                  </div>
                </div>

                <!-- HTML Body -->
                <div class="col-12 mb-3">
                  <div class="accordion" id="htmlBodyAccordion">
                    <div class="accordion-item">
                      <h2 class="accordion-header">
                        <button 
                          class="accordion-button"
                          :class="{ 'collapsed': !isHtmlBodyExpanded }"
                          type="button"
                          data-bs-toggle="collapse"
                          data-bs-target="#htmlBodyCollapse"
                          :aria-expanded="isHtmlBodyExpanded"
                          aria-controls="htmlBodyCollapse"
                        >
                          <span>
                            {{ $t('databank.mailTemplate.htmlBody') }}
                            <span class="text-danger">*</span>
                          </span>
                        </button>
                      </h2>
                      <div 
                        id="htmlBodyCollapse" 
                        class="accordion-collapse collapse show"
                        :class="{ 'show': isHtmlBodyExpanded }"
                        data-bs-parent="#htmlBodyAccordion"
                      >
                        <div class="accordion-body p-0">
                          <!-- Required Placeholders Badges -->
                          <div v-if="initialRequiredPlaceholders.length > 0" class="p-3 border-bottom">
                            <small class="text-muted d-block mb-1">
                              <strong>{{ $t('databank.mailTemplate.requiredPlaceholders') }}:</strong>
                            </small>
                            <div class="d-flex flex-wrap gap-2">
                              <span 
                                v-for="placeholder in initialRequiredPlaceholders" 
                                :key="placeholder"
                                class="badge text-white"
                                :class="isPlaceholderMissing(placeholder) ? 'bg-danger' : 'placeholder-found'"
                              >
                                {{ placeholder }}
                              </span>
                            </div>
                          </div>
                          <!-- Code Editor and Preview Side by Side -->
                          <div class="row g-0" style="height: calc(100vh - 400px); min-height: 500px;">
                            <!-- Code Editor Column -->
                            <div class="col-md-6 border-end d-flex flex-column">
                              <div class="p-2 border-bottom bg-light">
                                <small class="text-muted fw-bold">Code Editor</small>
                              </div>
                              <div class="flex-grow-1 position-relative">
                                <div 
                                  ref="codeEditorContainer"
                                  :class="{ 'is-invalid': v$.htmlBody.$error }"
                                  class="code-editor-wrapper h-100"
                                ></div>
                                <div v-if="v$.htmlBody.$error" class="invalid-feedback position-absolute bottom-0 start-0 p-2">
                                  {{ v$.htmlBody.$errors[0].$message }}
                                </div>
                              </div>
                            </div>
                            <!-- Preview Column -->
                            <div class="col-md-6 d-flex flex-column">
                              <div class="p-2 border-bottom bg-light d-flex justify-content-between align-items-center">
                                <small class="text-muted fw-bold">Preview</small>
                                <button 
                                  type="button"
                                  class="btn btn-sm btn-outline-info"
                                  @click="showPreviewFromForm"
                                  :disabled="!form.htmlBody"
                                  title="Open in fullscreen preview"
                                >
                                  <i class="ph-duotone ph-arrows-out me-1"></i>
                                  Fullscreen
                                </button>
                              </div>
                              <div class="flex-grow-1 position-relative bg-light">
                                <div v-if="!form.htmlBody" class="d-flex align-items-center justify-content-center h-100 text-muted">
                                  <div class="text-center">
                                    <i class="ph-duotone ph-eye-slash" style="font-size: 3rem; opacity: 0.3;"></i>
                                    <p class="mt-2 mb-0">No content to preview</p>
                                  </div>
                                </div>
                                <iframe
                                  v-else
                                  :srcdoc="form.htmlBody"
                                  ref="previewIframe"
                                  class="w-100 h-100 border-0"
                                  sandbox="allow-same-origin"
                                  style="min-height: 100%;"
                                ></iframe>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </form>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">
              {{ $t('common.cancel') }}
            </button>
            <button 
              type="button"
              class="btn btn-info"
              @click="handleSave(false)"
              :disabled="isLoading || !allPlaceholdersPresent"
            >
              <span v-if="isLoading" class="spinner-border spinner-border-sm me-1"></span>
              <i class="ph-duotone ph-floppy-disk me-2"></i>
              {{ isLoading ? $t('common.saving') : $t('common.save') }}
            </button>
            <button 
              type="button"
              class="btn btn-primary"
              @click="handleSave(true)"
              :disabled="isLoading || !allPlaceholdersPresent"
            >
              <span v-if="isLoading" class="spinner-border spinner-border-sm me-1"></span>
              <i class="ph-duotone ph-floppy-disk me-2"></i>
              {{ isLoading ? $t('common.savingAndClosing') : $t('common.saveAndClose') }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- HTML Preview Modal -->
    <div class="modal fade" id="htmlPreviewModal" tabindex="-1" aria-labelledby="htmlPreviewModalLabel" aria-hidden="true">
      <div class="modal-dialog modal-fullscreen">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="htmlPreviewModalLabel">
              {{ $t('databank.mailTemplate.previewTitle') }} - {{ previewTemplateKey }}
            </h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body p-0">
            <div v-if="isPreviewLoading" class="text-center py-5">
              <div class="spinner-border text-primary" role="status">
                <span class="visually-hidden">{{ $t('common.loading') }}</span>
              </div>
            </div>
            <iframe
              v-else
              :srcdoc="previewHtmlContent"
              class="w-100 border-0"
              style="height: calc(100vh - 60px);"
              sandbox="allow-same-origin"
            ></iframe>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">
              {{ $t('common.close') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch, nextTick, onBeforeUnmount } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import useVuelidate from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import mailTemplateService from '@/services/mailTemplateService';
import { Modal } from 'bootstrap';
import Swal from 'sweetalert2';
import { EditorView, keymap } from '@codemirror/view';
import { EditorState } from '@codemirror/state';
import { basicSetup } from 'codemirror';
import { html } from '@codemirror/lang-html';
import { defaultKeymap } from '@codemirror/commands';

const store = useStore();
const router = useRouter();
const { t } = useI18n();

const error = ref(null);
const success = ref(null);
const isLoading = ref(false);
const isLoadingData = ref(false);
const isEditing = ref(false);
const editingId = ref(null);
const searchQuery = ref('');
const isPreviewLoading = ref(false);
const previewHtmlContent = ref('');
const previewTemplateKey = ref('');
const codeEditorContainer = ref(null);
const previewIframe = ref(null);
const isHtmlBodyExpanded = ref(true);
const initialRequiredPlaceholders = ref([]);
let codeEditor = null;
let mailTemplateModal = null;
let htmlPreviewModal = null;
let accordionShowHandler = null;
let accordionHideHandler = null;

const form = ref({
  templateKey: '',
  subject: '',
  htmlBody: ''
});

const mailTemplates = ref([]);

const currentUser = computed(() => store.getters['auth/currentUser']);

// Validation rules
const rules = computed(() => ({
  templateKey: { required },
  subject: { required },
  htmlBody: { required }
}));

const v$ = useVuelidate(rules, form);

// Use mail templates directly since search is handled by API
const filteredMailTemplates = computed(() => {
  return mailTemplates.value;
});

// Watch for search query changes
let searchTimeout;
watch(searchQuery, (newQuery) => {
  clearTimeout(searchTimeout);
  searchTimeout = setTimeout(() => {
    if (newQuery.trim()) {
      loadMailTemplates(newQuery.trim());
    } else {
      loadMailTemplates();
    }
  }, 500); // 500ms debounce
});

onMounted(async () => {
  // Check if user is super admin
  if (!currentUser.value?.isSuperAdmin) {
    store.dispatch('notify', {
      message: t('common.notification.unauthorized'),
      type: 'error'  
    });
    router.push('/databank');
  }
  
  // Initialize modals
  const modalElement = document.getElementById('mailTemplateModal');
  if (modalElement) {
    mailTemplateModal = new Modal(modalElement);
    
    // Initialize CodeMirror when modal is shown
    modalElement.addEventListener('shown.bs.modal', () => {
      nextTick(() => {
        initCodeEditor();
        // Sync accordion state with Bootstrap collapse events
        const htmlBodyCollapse = document.getElementById('htmlBodyCollapse');
        if (htmlBodyCollapse) {
          // Remove existing listeners if any
          if (accordionShowHandler) {
            htmlBodyCollapse.removeEventListener('show.bs.collapse', accordionShowHandler);
          }
          if (accordionHideHandler) {
            htmlBodyCollapse.removeEventListener('hide.bs.collapse', accordionHideHandler);
          }
          
          // Add new listeners
          accordionShowHandler = () => {
            isHtmlBodyExpanded.value = true;
          };
          accordionHideHandler = () => {
            isHtmlBodyExpanded.value = false;
          };
          
          htmlBodyCollapse.addEventListener('show.bs.collapse', accordionShowHandler);
          htmlBodyCollapse.addEventListener('hide.bs.collapse', accordionHideHandler);
          
          // Sync initial state based on Bootstrap's show class
          const updateState = () => {
            isHtmlBodyExpanded.value = htmlBodyCollapse.classList.contains('show');
          };
          updateState();
        }
      });
    });
    
    // Cleanup CodeEditor when modal is hidden
    modalElement.addEventListener('hidden.bs.modal', () => {
      destroyCodeEditor();
      resetForm();
      // Remove accordion event listeners
      const htmlBodyCollapse = document.getElementById('htmlBodyCollapse');
      if (htmlBodyCollapse) {
        if (accordionShowHandler) {
          htmlBodyCollapse.removeEventListener('show.bs.collapse', accordionShowHandler);
        }
        if (accordionHideHandler) {
          htmlBodyCollapse.removeEventListener('hide.bs.collapse', accordionHideHandler);
        }
      }
    });
  }
  
  const previewModalElement = document.getElementById('htmlPreviewModal');
  if (previewModalElement) {
    htmlPreviewModal = new Modal(previewModalElement);
  }
  
  loadMailTemplates();
});

const initCodeEditor = () => {
  if (!codeEditorContainer.value || codeEditor) return;
  
  try {
    const state = EditorState.create({
      doc: form.value.htmlBody || '',
      extensions: [
        basicSetup,
        html(),
        keymap.of(defaultKeymap),
        EditorView.updateListener.of((update) => {
          if (update.docChanged) {
            const content = update.state.doc.toString();
            form.value.htmlBody = content;
          }
        }),
        EditorView.theme({
          '&': {
            fontSize: '14px',
            border: 'none',
            borderRadius: '0'
          },
          '.cm-content': {
            padding: '12px'
          },
          '.cm-focused': {
            outline: 'none'
          },
          '.cm-scroller': {
            fontFamily: 'monospace'
          }
        })
      ]
    });
    
    codeEditor = new EditorView({
      state,
      parent: codeEditorContainer.value
    });
  } catch (err) {
    console.error('Error initializing code editor:', err);
  }
};

const destroyCodeEditor = () => {
  if (codeEditor) {
    try {
      codeEditor.destroy();
      codeEditor = null;
    } catch (err) {
      console.error('Error destroying code editor:', err);
    }
  }
};

onBeforeUnmount(() => {
  destroyCodeEditor();
  // Clear preview update timer
  if (previewUpdateTimer) {
    clearTimeout(previewUpdateTimer);
  }
});

// Debounce timer for preview updates
let previewUpdateTimer = null;

// Watch form.htmlBody to update editor when form is loaded
watch(() => form.value.htmlBody, (newValue) => {
  if (codeEditor && codeEditor.state.doc.toString() !== newValue) {
    const transaction = codeEditor.state.update({
      changes: {
        from: 0,
        to: codeEditor.state.doc.length,
        insert: newValue || ''
      }
    });
    codeEditor.dispatch(transaction);
  }
  
  // Debounce preview updates to avoid performance issues
  clearTimeout(previewUpdateTimer);
  previewUpdateTimer = setTimeout(() => {
    nextTick(() => {
      if (previewIframe.value && newValue) {
        try {
          const iframeDoc = previewIframe.value.contentDocument || previewIframe.value.contentWindow.document;
          if (iframeDoc) {
            iframeDoc.open();
            iframeDoc.write(newValue);
            iframeDoc.close();
          }
        } catch (err) {
          // If cross-origin or other error, force iframe reload by updating srcdoc
          if (previewIframe.value) {
            previewIframe.value.srcdoc = newValue;
          }
        }
      }
    });
  }, 300); // 300ms debounce
});

// Extract required placeholders from HTML body (excluding CSS)
const requiredPlaceholders = computed(() => {
  if (!form.value.htmlBody) return [];
  
  // Remove CSS blocks first to avoid matching CSS properties
  let htmlContent = form.value.htmlBody;
  
  // Remove style tags and their content
  htmlContent = htmlContent.replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '');
  
  // Remove inline styles (style="...")
  htmlContent = htmlContent.replace(/style\s*=\s*["'][^"']*["']/gi, '');
  
  // Remove CSS within style attributes more thoroughly
  htmlContent = htmlContent.replace(/style\s*=\s*\{[^}]*\}/gi, '');
  
  // Remove CSS property definitions like { color: red; } or {display: block}
  // This matches { followed by optional whitespace, then a colon or semicolon
  htmlContent = htmlContent.replace(/\{[^}]*[:;][^}]*\}/g, '');
  
  // Now find placeholders - match {word} pattern
  // Pattern: { followed by word characters (letters, numbers, underscore), not followed by colon or semicolon
  const placeholderPattern = /\{([a-zA-Z][a-zA-Z0-9_]*)\}/g;
  const matches = [];
  let match;
  
  while ((match = placeholderPattern.exec(htmlContent)) !== null) {
    const placeholder = `{${match[1]}}`;
    if (!matches.includes(placeholder)) {
      matches.push(placeholder);
    }
  }
  
  return matches.sort();
});

// Check if a placeholder is missing from current HTML body
const isPlaceholderMissing = (placeholder) => {
  if (!form.value.htmlBody || !placeholder) return true; // If no body, consider missing
  // Check if placeholder exists in current HTML body
  return !form.value.htmlBody.includes(placeholder);
};

// Check if all required placeholders are present
const allPlaceholdersPresent = computed(() => {
  if (initialRequiredPlaceholders.value.length === 0) return true; // No required placeholders, allow save
  // Check if all initial placeholders are present in current HTML body
  return initialRequiredPlaceholders.value.every(placeholder => !isPlaceholderMissing(placeholder));
});

// Extract placeholders from HTML content (helper function)
const extractPlaceholders = (htmlContent) => {
  if (!htmlContent) return [];
  
  // Remove CSS blocks first to avoid matching CSS properties
  let content = htmlContent;
  
  // Remove style tags and their content
  content = content.replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '');
  
  // Remove inline styles (style="...")
  content = content.replace(/style\s*=\s*["'][^"']*["']/gi, '');
  
  // Remove CSS within style attributes more thoroughly
  content = content.replace(/style\s*=\s*\{[^}]*\}/gi, '');
  
  // Remove CSS property definitions like { color: red; } or {display: block}
  content = content.replace(/\{[^}]*[:;][^}]*\}/g, '');
  
  // Now find placeholders - match {word} pattern
  const placeholderPattern = /\{([a-zA-Z][a-zA-Z0-9_]*)\}/g;
  const matches = [];
  let match;
  
  while ((match = placeholderPattern.exec(content)) !== null) {
    const placeholder = `{${match[1]}}`;
    if (!matches.includes(placeholder)) {
      matches.push(placeholder);
    }
  }
  
  return matches.sort();
};

const loadMailTemplates = async (searchText = '') => {
  isLoadingData.value = true;
  
  try {
    let response;
    
    // Handle case where event object is passed instead of string
    const searchQuery = typeof searchText === 'string' ? searchText : '';
    
    if (searchQuery) {
      // Use search API when there's a search query
      response = await mailTemplateService.searchMailTemplates({
        searchText: searchQuery,
        page: 0,
        pageSize: 100
      });
    } else {
      // Load all mail templates
      response = await mailTemplateService.searchMailTemplates({
        searchText: '',
        page: 0,
        pageSize: 100
      });
    }
    
    if (response && response.items) {
      // Handle paginated response
      mailTemplates.value = response.items.map(template => ({
        id: template.id,
        templateKey: template.templateKey || template.key,
        subject: template.subject,
        htmlBody: template.htmlBody,
        isActive: template.isActive !== undefined ? template.isActive : true,
        createdAt: template.creationTime || template.createdAt
      }));
    } else if (response && Array.isArray(response)) {
      mailTemplates.value = response.map(template => ({
        id: template.id,
        templateKey: template.templateKey || template.key,
        subject: template.subject,
        htmlBody: template.htmlBody,
        isActive: template.isActive !== undefined ? template.isActive : true,
        createdAt: template.creationTime || template.createdAt
      }));
    } else {
      mailTemplates.value = [];
    }
    
  } catch (err) {
    console.error('Error loading mail templates:', err);
    error.value = t('databank.mailTemplate.errors.loadError');
    
    store.dispatch('notify', {
      message: error.value,
      type: 'error'
    });
  } finally {
    isLoadingData.value = false;
  }
};

const handleSave = async (shouldClose = false) => {
  // Validate form
  const isFormValid = await v$.value.$validate();
  if (!isFormValid) return;
  
  isLoading.value = true;
  let savedSuccessfully = false;
  let savedTemplate = null;
  error.value = null;
  success.value = null;
  
  try {
    const templateData = {
      templateKey: form.value.templateKey,
      subject: form.value.subject,
      htmlBody: form.value.htmlBody
    };
    
    if (isEditing.value) {
      // Update existing mail template
      const response = await mailTemplateService.updateMailTemplate(editingId.value, templateData);
      
      if (response) {
        savedTemplate = response;
        // Update local array
        const index = mailTemplates.value.findIndex(t => t.id === editingId.value);
        if (index !== -1) {
          mailTemplates.value[index] = {
            ...mailTemplates.value[index],
            templateKey: response.templateKey || response.key,
            subject: response.subject,
            htmlBody: response.htmlBody,
            isActive: response.isActive !== undefined ? response.isActive : true
          };
        }
        success.value = t('databank.mailTemplate.updateSuccess');
      }
    } else {
      // Create new mail template
      const response = await mailTemplateService.createMailTemplate(templateData);
      
      if (response) {
        savedTemplate = response;
        // Add to local array
        const newTemplate = {
          id: response.id,
          templateKey: response.templateKey || response.key,
          subject: response.subject,
          htmlBody: response.htmlBody,
          isActive: response.isActive !== undefined ? response.isActive : true,
          createdAt: response.creationTime || new Date()
        };
        mailTemplates.value.unshift(newTemplate);
        success.value = t('databank.mailTemplate.createSuccess');
      }
    }
    
    savedSuccessfully = true;
    
    store.dispatch('notify', {
      message: success.value,
      type: 'success'
    });
    
    // If closing, hide the modal
    if (shouldClose && mailTemplateModal) {
      mailTemplateModal.hide();
      resetForm();
    }
    
  } catch (err) {
    console.error('Submit error:', err);
    error.value = t('databank.mailTemplate.errors.saveError');
    
    store.dispatch('notify', {
      message: error.value,
      type: 'error'
    });
  } finally {
    isLoading.value = false;
  }
  
  // Post-Save Actions (only if save was successful and modal is staying open)
  if (savedSuccessfully && !shouldClose) {
    if (!isEditing.value && savedTemplate) {
      // If creating and NOT closing, switch to edit mode for the newly created template
      form.value = {
        templateKey: savedTemplate.templateKey || savedTemplate.key || '',
        subject: savedTemplate.subject || '',
        htmlBody: savedTemplate.htmlBody || ''
      };
      isEditing.value = true;
      editingId.value = savedTemplate.id;
      
      // Save initial required placeholders for newly created template
      if (savedTemplate.htmlBody) {
        initialRequiredPlaceholders.value = extractPlaceholders(savedTemplate.htmlBody);
      } else {
        initialRequiredPlaceholders.value = [];
      }
    } else if (isEditing.value && savedTemplate) {
      // Update initial placeholders if template was updated
      if (savedTemplate.htmlBody) {
        initialRequiredPlaceholders.value = extractPlaceholders(savedTemplate.htmlBody);
      }
    }
  }
};

const editMailTemplate = async (template) => {
  isLoading.value = true;
  error.value = null;
  
  try {
    // Fetch full template data using GET endpoint
    const response = await mailTemplateService.getMailTemplateById(template.id);
    
    if (response) {
      form.value = {
        templateKey: response.templateKey || response.key || '',
        subject: response.subject || '',
        htmlBody: response.htmlBody || ''
      };
      isEditing.value = true;
      editingId.value = template.id;
      
      // Save initial required placeholders when template is loaded
      if (response.htmlBody) {
        initialRequiredPlaceholders.value = extractPlaceholders(response.htmlBody);
      } else {
        initialRequiredPlaceholders.value = [];
      }
      
      if (mailTemplateModal) {
        mailTemplateModal.show();
        await nextTick();
        // Ensure accordion is expanded when editing
        isHtmlBodyExpanded.value = true;
        initCodeEditor();
      }
    }
  } catch (err) {
    console.error('Error loading mail template:', err);
    error.value = t('databank.mailTemplate.errors.loadError');
    
    store.dispatch('notify', {
      message: error.value,
      type: 'error'
    });
  } finally {
    isLoading.value = false;
  }
};

const deleteMailTemplate = async (id) => {
  if (!confirm(t('databank.mailTemplate.confirmDelete'))) return;
  
  try {
    await mailTemplateService.deleteMailTemplate(id);
    
    mailTemplates.value = mailTemplates.value.filter(t => t.id !== id);
    
    store.dispatch('notify', {
      message: t('databank.mailTemplate.deleteSuccess'),
      type: 'success'
    });
    
  } catch (err) {
    console.error('Delete error:', err);
    store.dispatch('notify', {
      message: t('databank.mailTemplate.errors.deleteError'),
      type: 'error'
    });
  }
};

const restoreMailTemplate = async (template) => {
  if (!template.templateKey) {
    store.dispatch('notify', {
      message: t('databank.mailTemplate.errors.invalidTemplateKey'),
      type: 'error'
    });
    return;
  }

  // Show confirmation dialog with warnings
  const result = await Swal.fire({
    title: t('databank.mailTemplate.restoreConfirmTitle'),
    html: `
      <div class="text-start">
        <p>${t('databank.mailTemplate.restoreConfirmMessage')}</p>
        <p class="text-danger mt-3"><strong>${t('databank.mailTemplate.restoreWarning')}</strong></p>
        <p class="text-muted mt-2">${t('databank.mailTemplate.restoreWarningDetails')}</p>
      </div>
    `,
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#d33',
    cancelButtonColor: '#6c757d',
    confirmButtonText: t('databank.mailTemplate.restoreConfirmButton'),
    cancelButtonText: t('common.cancel'),
    customClass: {
      confirmButton: 'btn btn-danger',
      cancelButton: 'btn btn-outline-secondary'
    },
    buttonsStyling: false
  });

  if (!result.isConfirmed) {
    return;
  }

  isLoading.value = true;
  
  try {
    await mailTemplateService.restoreHostTemplateByKey(template.templateKey);
    
    // Reload the template list to show updated data
    await loadMailTemplates();
    
    store.dispatch('notify', {
      message: t('databank.mailTemplate.restoreSuccess'),
      type: 'success'
    });
    
  } catch (err) {
    console.error('Restore error:', err);
    store.dispatch('notify', {
      message: t('databank.mailTemplate.errors.restoreError'),
      type: 'error'
    });
  } finally {
    isLoading.value = false;
  }
};

const resetForm = () => {
  form.value = {
    templateKey: '',
    subject: '',
    htmlBody: ''
  };
  isEditing.value = false;
  editingId.value = null;
  isHtmlBodyExpanded.value = true;
  initialRequiredPlaceholders.value = [];
  v$.value.$reset();
  // Reset code editor content
  if (codeEditor) {
    const transaction = codeEditor.state.update({
      changes: {
        from: 0,
        to: codeEditor.state.doc.length,
        insert: ''
      }
    });
    codeEditor.dispatch(transaction);
  }
};

const showHtmlPreview = async (template) => {
  isPreviewLoading.value = true;
  previewTemplateKey.value = template.templateKey || template.key || '';
  
  try {
    // If template has htmlBody, use it directly, otherwise fetch from API
    if (template.htmlBody) {
      previewHtmlContent.value = template.htmlBody;
    } else {
      const response = await mailTemplateService.getMailTemplateById(template.id);
      previewHtmlContent.value = response?.htmlBody || '';
    }
    
    if (htmlPreviewModal) {
      htmlPreviewModal.show();
    }
  } catch (err) {
    console.error('Error loading preview:', err);
    store.dispatch('notify', {
      message: t('databank.mailTemplate.errors.previewError'),
      type: 'error'
    });
  } finally {
    isPreviewLoading.value = false;
  }
};

const showPreviewFromForm = () => {
  if (!form.value.htmlBody) return;
  
  previewTemplateKey.value = form.value.templateKey || '';
  previewHtmlContent.value = form.value.htmlBody;
  isPreviewLoading.value = false;
  
  if (htmlPreviewModal) {
    htmlPreviewModal.show();
  }
};

const formatDate = (date) => {
  if (!date) return '-';
  return new Date(date).toLocaleDateString();
};

const clearSearch = () => {
  searchQuery.value = '';
};
</script>

<style scoped>
.card-header {
  background: white;
  color: #212529;
}

.card-header h5,
.card-header h6,
.card-header i,
.card-header * {
  color: #212529 !important;
}

.card-header .form-control {
  color: #212529 !important;
}

.btn-link {
  color: #6c757d !important;
  font-size: 1.25rem;
  font-weight: 500;
}

.btn-link:hover {
  color: #495057 !important;
}

.code-editor-wrapper {
  border: none;
  border-radius: 0;
  overflow: hidden;
  height: 100%;
}

.code-editor-wrapper.is-invalid {
  border: 1px solid #dc3545;
}

.code-editor-wrapper :deep(.cm-editor) {
  height: 100%;
}

.code-editor-wrapper :deep(.cm-content) {
  min-height: auto;
  height: 100%;
}

.code-editor-wrapper :deep(.cm-scroller) {
  overflow: auto;
  height: 100%;
}

.placeholder-found {
  background-color: #198754 !important; /* Darker green */
}
</style>

